import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import brandLockup from '../asset/logo/lockup-horizontal-color.svg';
import {
    BLUE_TEXT, BORDER, INTER, PILOT_REQUEST_HREF, PUBLIC_CONTENT_MAX_WIDTH, PUBLIC_PAGE_GUTTER, SLATE, SUPPORT_EMAIL,
    primaryButtonSx,
} from '../style/verbaScholaPublicStyles';
import { SignedInAccount } from './verbaScholaLanding';

interface PublicHeaderProps {
    /** The login page shows only the brand, so nothing competes with its form. */
    minimal?: boolean;
    account?: SignedInAccount | null;
    accountPending?: boolean;
    accountUnavailable?: boolean;
    onRetryAccount?: () => void;
}

// Public pages carry only the brand and account entry points; product navigation
// belongs to the signed-in student and teacher shells.
export function VerbaScholaPublicHeader({ minimal = false, account = null, accountPending = false, accountUnavailable = false, onRetryAccount }: PublicHeaderProps) {
    return (
        <Box component="header" sx={{
            position: 'sticky', top: 0, zIndex: 5,
            backgroundColor: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${BORDER}`,
        }}>
            <Stack direction="row" alignItems="center" sx={{ maxWidth: PUBLIC_CONTENT_MAX_WIDTH, mx: 'auto', minHeight: 72, px: PUBLIC_PAGE_GUTTER }}>
                <Box component={RouterLink} to="/" aria-label="VerbaSchola home" sx={{ display: 'flex', borderRadius: '8px' }}>
                    <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: { xs: 22, sm: 26 }, width: 'auto', display: 'block' }} />
                </Box>
                {!minimal && <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                    {account ? (
                        <Button component={RouterLink} to={account.landing} sx={{ ...primaryButtonSx, px: 2.5 }}>Open VerbaSchola</Button>
                    ) : accountPending ? (
                        <Button onClick={accountUnavailable ? onRetryAccount : undefined} disabled={!accountUnavailable}
                            sx={{ ...primaryButtonSx, px: 2.5 }}>
                            {accountUnavailable ? 'Retry account' : 'Loading account…'}
                        </Button>
                    ) : (
                        <>
                            <Button component={RouterLink} to="/login" variant="text" sx={{ px: 2 }}>Log in</Button>
                            <Button component={RouterLink} to={PILOT_REQUEST_HREF} sx={{ ...primaryButtonSx, px: 2.5, display: { xs: 'none', sm: 'inline-flex' } }}>Request a pilot</Button>
                        </>
                    )}
                </Stack>}
            </Stack>
        </Box>
    );
}

const footerLinkSx = { fontFamily: INTER, fontSize: 14, fontWeight: 600, color: BLUE_TEXT, minHeight: 44, display: 'inline-flex', alignItems: 'center' } as const;

export function VerbaScholaPublicFooter() {
    return (
        <Box component="footer" sx={{ backgroundColor: '#FFFFFF', borderTop: `1px solid ${BORDER}` }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ maxWidth: PUBLIC_CONTENT_MAX_WIDTH, mx: 'auto', px: PUBLIC_PAGE_GUTTER, py: 4 }}>
                <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: 22, width: 'auto' }} />
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>
                    Speaking practice for language classrooms.
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ ml: { sm: 'auto' } }}>
                    <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" sx={footerLinkSx}>Help &amp; support</Link>
                    <Link component={RouterLink} to="/login" underline="hover" sx={footerLinkSx}>Log in</Link>
                    <Link component={RouterLink} to={PILOT_REQUEST_HREF} underline="hover" sx={footerLinkSx}>Request a pilot</Link>
                </Stack>
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center', pb: 3, px: 2 }}>
                © {new Date().getFullYear()} VerbaSchola Inc.
            </Typography>
        </Box>
    );
}
